import { z } from 'zod';
/** Which Kirin client a `/api/releases/check` query refers to (KIR-2962). */
export const clientKindSchema = z.enum(['agent', 'cursor_ext', 'copilot_ext']);
// KIR-2962 / KIR-4025: latest available version per Kirin client, served by
// GET /api/releases/latest. The backend fetches + caches these from the release
// pipeline (agent) and the extension marketplaces (Cursor/Copilot) so every
// client and the dashboard read one shared answer.
/** Where a "latest" answer came from — for debugging / provenance. */
export const releaseSourceSchema = z.enum([
    'manifest', // authoritative GA manifest (latest.json) — KIR-4275
    'openvsx', // fallback: OpenVSX (published = GA)
]);
/** Latest release info for one client. */
export const clientReleaseSchema = z.object({
    /** Latest published/released version; null when the source is unavailable. */
    latestVersion: z.string().nullable(),
    /** Where to get it: the agent installer URL, or the extension's marketplace page. */
    downloadUrl: z.string().url().nullable(),
    source: releaseSourceSchema.nullable(),
});
// KIR-4071 / KIR-4558: SHA-256 hashes of the released installer artifacts, so
// customers can EDR-allow-list Kirin by file hash (Vista / SentinelOne driver).
// Global (not per-env/per-client): a signed installer's bytes are identical
// everywhere it's served. Produced by the release pipelines (KIR-4558) into the
// manifest as a rolling history of the current version + a few prior, so a
// customer on a slightly older build can still find their file's hash.
/** A 64-char lowercase-hex SHA-256 digest. */
export const sha256HexSchema = z.string().regex(/^[a-f0-9]{64}$/);
/** Filename → SHA-256 for every distributed artifact of one release. */
export const installerArtifactHashesSchema = z.record(z.string(), sha256HexSchema);
/** One release's artifact hashes, labeled by version. */
export const installerVersionHashesSchema = z.object({
    version: z.string(),
    artifacts: installerArtifactHashesSchema,
});
/**
 * Installer hashes carried on GET /api/releases/latest. `versions` is newest
 * first (current, then prior), so `versions[0]` is the current release. Null
 * when the manifest carries no hashes yet (the producer KIR-4558 hasn't shipped,
 * or a total source outage).
 */
export const installerHashesSchema = z.object({
    /** Digest algorithm — SHA-256 is the allow-listing standard. */
    algorithm: z.literal('sha256'),
    versions: z.array(installerVersionHashesSchema),
});
/**
 * Extensions EDR actually scans — the standalone agent installers a customer
 * allow-lists. IDE extension packages (`.vsix`) install into the editor rather
 * than run as executables an EDR flags, so they are left out. Shared by the
 * Install tab's checksum list and `/api/v1` agent releases (KIR-5145) so the two
 * cannot list different files.
 */
const INSTALLER_FILE = /\.(pkg|exe|msi|deb|rpm)$/i;
/** Whether `file` is a standalone installer surfaced for allow-listing. */
export function isInstallerFile(file) {
    return INSTALLER_FILE.test(file);
}
/** Response of GET /api/releases/latest — latest per client. */
export const releasesLatestSchema = z.object({
    agent: clientReleaseSchema,
    cursor_ext: clientReleaseSchema,
    copilot_ext: clientReleaseSchema,
    /**
     * Installer artifact hashes for EDR allow-listing (KIR-4071). `.nullish()`,
     * not just `.nullable()`: an older backend during a rolling deploy omits the
     * key entirely, and the client (which also drives Inventory update-available)
     * must treat that as "no hashes" rather than failing to parse the response.
     * null (published as absent) and undefined (omitted) both mean no hashes.
     *
     * `.catch(null)` isolates malformed hash data: a bad digest / shape in the
     * manifest degrades this one field to "no hashes" instead of failing the whole
     * /releases/latest parse — the download links and update-available signal this
     * response also carries must survive a hash-only defect. Absent → no hashes,
     * malformed → no hashes; either way the UI just renders no hashes (fail-safe).
     */
    installerHashes: installerHashesSchema.nullish().catch(null),
});
// KIR-2962 / KIR-4026: GET /api/releases/check — the comparator-less "am I
// behind?" endpoint for the Go agent (which has no semver) and the IDE. The
// caller passes its client + installed version; the backend returns the latest
// for that client plus a server-computed `updateAvailable`, so every endpoint
// renders a boolean rather than running its own comparator.
/** Query for GET /api/releases/check. `client` defaults to the agent. */
export const releaseCheckQuerySchema = z.object({
    installed: z.string().min(1),
    client: clientKindSchema.default('agent'),
});
/** Result of GET /api/releases/check for the caller's client. */
export const releaseCheckResultSchema = z.object({
    /** Latest version for the caller's client; null when the backend has none. */
    latestVersion: z.string().nullable(),
    /** True only when the installed version is confidently behind latest. */
    updateAvailable: z.boolean(),
});
//# sourceMappingURL=releases.js.map